// Таймаут промежуточных дел (новая серверная система): бэк дело сам не
// останавливает — remainingSeconds уходит в минус. Сохраняем прежний UX:
// по достижении дедлайна ставим паузу через API и открываем модалку выбора
// следующего действия с обязательным вводом результата.
import { useEffect, useRef } from 'react';

import {
  pauseIntermediate,
  useIntermediateStore,
} from '@/entities/intermediate';
import { useTaskSelectionModalStore } from '@/entities/task/model/stores/task-selection-modal';
import { useTaskTimeoutModalStore } from '@/entities/task/model/stores/task-timeout-modal';

export function useIntermediateTimeMonitor() {
  const items = useIntermediateStore((s) => s.items);
  const openSelection = useTaskSelectionModalStore((s) => s.openModal);
  const selectionOpen = useTaskSelectionModalStore((s) => s.isOpen);
  const timeoutOpen = useTaskTimeoutModalStore((s) => s.isOpen);
  // Дела, для которых модалка уже показывалась, — не дёргаем повторно
  // (пользователь мог продлить план или осознанно оставить просрочку).
  const handledRef = useRef<Set<number>>(new Set());
  const inFlightRef = useRef(false);

  useEffect(() => {
    // План продлили (+время) или дело ушло из списка — снимаем пометку,
    // чтобы следующая просрочка снова показала модалку.
    for (const id of Array.from(handledRef.current)) {
      const it = items.find((x) => x.taskId === id);
      if (!it || it.remainingSeconds > 0) {
        handledRef.current.delete(id);
      }
    }

    if (selectionOpen || timeoutOpen || inFlightRef.current) {
      return;
    }

    const expired = items.find(
      (it) =>
        it.status === 'running' &&
        it.remainingSeconds <= 0 &&
        !handledRef.current.has(it.taskId)
    );
    if (!expired) {
      return;
    }

    handledRef.current.add(expired.taskId);
    inFlightRef.current = true;

    void (async () => {
      try {
        const state = await pauseIntermediate(expired.taskId);
        useIntermediateStore.getState().upsert(state);
      } catch (e) {
        // Даже если пауза не прошла (например, 502) — модалку показываем:
        // время на бэке не теряется, а пользователь должен отреагировать.
        console.error('pauseIntermediate on timeout failed', e);
      } finally {
        inFlightRef.current = false;
      }

      // Пользователь мог сам успеть открыть модалку паузы — не перехватываем.
      if (!useTaskSelectionModalStore.getState().isOpen) {
        openSelection({
          mode: 'intermediate-timeup',
          taskId: String(expired.taskId),
        });
      }
    })();
  }, [items, selectionOpen, timeoutOpen, openSelection]);
}
